
import React, { useState, useEffect } from 'react';
import SearchSection from '../SearchSection';
import SectionManager from './components/SectionManager';
import ProgressIndicator from './components/ProgressIndicator';
import FormContentFooter from './components/FormContentFooter';
import { FormSection, BusinessDetails } from '../types';

interface ModalContentProps {
  formSection: FormSection;
  businessDetails: BusinessDetails;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (value: string, field: string) => void;
  handleDateSelect: (date: Date | undefined) => void;
  calendarOpen: boolean;
  setCalendarOpen: (open: boolean) => void;
  selectedDate?: Date;
  nameExceeds30Chars: boolean;
  shortNameLength: number;
  shortNameManuallyEdited: boolean;
  handleWebsiteBlur: () => void; // Required prop - ensures it's passed from AddBusinessModal
  showAddressSection?: boolean;
  showIndustrySection?: boolean;
  showSubmitButton?: boolean;
  handleSubmit?: () => void;
  handleOpenChange: (open: boolean) => void;
  handleBusinessSelect: (place: any) => void;
  handleCreateNew: () => void;
  isMobile?: boolean;
  basicInfoComplete?: boolean;
  addressComplete?: boolean;
  industryComplete?: boolean;
  basicInfoEdited?: boolean;
  addressEdited?: boolean;
  industryEdited?: boolean;
  basicInfoHasChanges?: boolean;
  addressHasChanges?: boolean;
  industryHasChanges?: boolean;
  onSaveBasicInfo?: () => void;
  onSaveAddress?: () => void;
  onSaveIndustry?: () => void;
  errors?: {
    phone?: string;
    email?: string;
    website?: string;
  };
}

const ModalContent: React.FC<ModalContentProps> = ({
  formSection,
  businessDetails,
  handleInputChange,
  handleSelectChange,
  handleDateSelect,
  calendarOpen,
  setCalendarOpen,
  selectedDate,
  nameExceeds30Chars,
  shortNameLength,
  shortNameManuallyEdited,
  handleWebsiteBlur,
  showAddressSection = false,
  showIndustrySection = false,
  showSubmitButton = false,
  handleSubmit,
  handleOpenChange,
  handleBusinessSelect,
  handleCreateNew,
  isMobile = false,
  basicInfoComplete = false,
  addressComplete = false,
  industryComplete = false,
  basicInfoEdited = false,
  addressEdited = false,
  industryEdited = false,
  basicInfoHasChanges = false,
  addressHasChanges = false,
  industryHasChanges = false,
  onSaveBasicInfo,
  onSaveAddress,
  onSaveIndustry,
  errors
}) => {
  // Local state for section open/close
  const [basicInfoOpen, setBasicInfoOpen] = useState(true);
  const [addressOpen, setAddressOpen] = useState(false);
  const [industryOpen, setIndustryOpen] = useState(false);
  
  // Update the accordion open states based on completion and editing status
  useEffect(() => {
    if (basicInfoEdited && !addressEdited && !industryEdited) {
      setBasicInfoOpen(false);
      setAddressOpen(true);
      setIndustryOpen(false);
    } else if (basicInfoEdited && addressEdited && !industryEdited) {
      setBasicInfoOpen(false);
      setAddressOpen(false);
      setIndustryOpen(true);
    }
  }, [basicInfoEdited, addressEdited, industryEdited]);

  // Log state for debugging
  useEffect(() => {
    console.log('ModalContent - basicInfoComplete:', basicInfoComplete);
  }, [basicInfoComplete]);

  if (formSection === 'search') {
    return (
      <div className="p-6 pt-2">
        <SearchSection 
          handleBusinessSelect={handleBusinessSelect}
          handleCreateNew={handleCreateNew}
          isMobile={isMobile}
        />
      </div>
    );
  }

  // Show the multi-step form with sections
  return (
    <div className="px-6 pt-2 pb-6">
      <ProgressIndicator 
        basicInfoComplete={basicInfoComplete}
        addressComplete={addressComplete}
        industryComplete={industryComplete}
        basicInfoEdited={basicInfoEdited}
        addressEdited={addressEdited}
        industryEdited={industryEdited}
      />
      
      <div className="mt-6">
        <SectionManager
          businessDetails={businessDetails}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          selectedDate={selectedDate}
          calendarOpen={calendarOpen}
          setCalendarOpen={setCalendarOpen}
          handleDateSelect={handleDateSelect}
          nameExceeds30Chars={nameExceeds30Chars}
          shortNameLength={shortNameLength}
          shortNameManuallyEdited={shortNameManuallyEdited}
          showAddressSection={showAddressSection}
          showIndustrySection={showIndustrySection}
          basicInfoOpen={basicInfoOpen}
          setBasicInfoOpen={setBasicInfoOpen}
          addressOpen={addressOpen}
          setAddressOpen={setAddressOpen}
          industryOpen={industryOpen}
          setIndustryOpen={setIndustryOpen}
          handleWebsiteBlur={handleWebsiteBlur}
          onSaveBasicInfo={onSaveBasicInfo}
          onSaveAddress={onSaveAddress}
          onSaveIndustry={onSaveIndustry}
          basicInfoComplete={basicInfoComplete}
          addressComplete={addressComplete}
          industryComplete={industryComplete}
          basicInfoEdited={basicInfoEdited}
          addressEdited={addressEdited}
          industryEdited={industryEdited}
          basicInfoHasChanges={basicInfoHasChanges}
          addressHasChanges={addressHasChanges}
          industryHasChanges={industryHasChanges}
          handleSubmit={handleSubmit}
          errors={errors}
        />
      </div>
      
      <FormContentFooter
        businessDetails={businessDetails}
        handleSubmit={handleSubmit}
        allStepsCompleted={showSubmitButton}
      />
    </div>
  );
};

export default ModalContent;
